import type {ReactElement} from 'react';
import {useEffect} from 'react';
import {useContext, useState} from 'react';
import {Button, IconPlacement, Size, Type} from '@Components/button';
import {EventWizardProgressStep, EventWizardSourceType, orderedProgressSteps} from '@Components/event-wizard/event-wizard.types';
import {SteppedProgressBar} from '@Components/stepped-progress-bar/stepped-progress-bar';
import {saveEvent} from '@Components/event-wizard/event-wizard.helper';
import {
  useEventWizardCoverPhoto,
  useEventWizardCurrentProgressStep,
  useEventWizardDescription,
  useEventWizardEndDateTime,
  useEventWizardOrganisers,
  useEventWizardPageSourceType,
  useEventWizardReduxState, useEventWizardRegistrationDetails,
  useEventWizardShouldNextBtnBeDisabled,
  useEventWizardStartDateTime,
  useEventWizardTimeZone,
  useEventWizardTitle,
  useEventWizardVenue,
} from '@Components/event-wizard/event-wizard.hooks';
import {
  onEventWizardCheckErrorStateUpdate,
  onEventWizardIsEventBeingCreatedUpdate, onEventWizardIsStylesPanelOpenUpdate,
  onEventWizardProgressStepUpdate,
  onEventWizardUpdateShouldExpandReviewSection,
} from '@Components/event-wizard/event-wizard-slice';
import {useDispatch} from 'react-redux';
import {
  isBasicSectionComplete,
  isCoverPhotoSectionComplete,
  isMediaSectionComplete,
  isOrganiserSectionComplete, isRegistrationsSectionComplete,
} from '@Components/event-wizard/event-wizard-review/event-wizard-review.helper';
import {EVENT_WIZARD_CONTAINER_QUERIES_CONTEXT} from '@Components/event-wizard/event-wizard-container-queries-context';
import {ProgressDots} from '@Components/progress-dots/progress-dots';
import {toggleDisplayPreviewCoverPhotoSection, toggleDisplayPreviewRegistrationForm} from '@Components/event-wizard/preview/preview.helper';
import styles from './event-footer.module.scss';
import {EventWizardTestId} from '@/test-ids/event-wizard-test-id';
import {authenticateUser} from '@Libraries/user.library';
import {openErrorModal} from "@Modals/error-modal";
import {GA4EventName} from "@Libraries/ga-events";
import {FeedbackButton} from '@Components/feedback-button';
import {TICKET_CHECKIN_SURVEY_HOTJAR_ID, TICKET_CHECKIN_SURVEY_URI_MARK_COMPLETE, TICKET_CHECKIN_SURVEY_URI_SHOW} from '@Libraries/events.library';

export function EventFooter(): ReactElement<any> {
  const dispatch = useDispatch();
  const eventWizardReduxState = useEventWizardReduxState();
  const currentStep = useEventWizardCurrentProgressStep();
  const currentStepIndex = orderedProgressSteps.indexOf(currentStep);
  const {isSmallScreen} = useContext(EVENT_WIZARD_CONTAINER_QUERIES_CONTEXT);

  const title = useEventWizardTitle();
  const startDateTime = useEventWizardStartDateTime();
  const endDateTime = useEventWizardEndDateTime();
  const timezone = useEventWizardTimeZone();
  const venue = useEventWizardVenue();
  const coverPhoto = useEventWizardCoverPhoto();
  const desc = useEventWizardDescription();
  const organisers = useEventWizardOrganisers();
  const shouldNextBtnBeDisabled = useEventWizardShouldNextBtnBeDisabled();
  const pageSourceType = useEventWizardPageSourceType();
  const registrationDetails = useEventWizardRegistrationDetails();

  const [isNextBtnLoading, setIsNextBtnLoading] = useState<boolean>(false);

  useEffect(() => {
    if (areRequirementsToSaveComplete()) {
      setIsNextBtnLoading(true);
      void authenticateUser(onAuthenticatedUserSaveEvent, resetLoadingStates, {
        panelText: window.i18next.t('pmwjs_make_account_to_share_event'),
        panelSubtext: window.i18next.t('pmwjs_login_event_promotion_subtext')
      });
    }
  }, [eventWizardReduxState.isEventBeingCreated, eventWizardReduxState.types])

  const onAuthenticatedUserSaveEvent = (): void => {
    void saveEvent(eventWizardReduxState, onSaveEventFail, pageSourceType);
  };

  const areRequirementsToSaveComplete = (): boolean => {
    return eventWizardReduxState.isEventBeingCreated &&
      !eventWizardReduxState.types.isAITypeLoading &&
      eventWizardReduxState.types.selectedType?.hashedID !== null;
  }

  const getStepLabels = (): string[] => {
    return [window.i18next.t('pmwjs_overview'), window.i18next.t('pmwjs_review')];
  };

  const onSaveEventFail = (showErrModal = false): void => {
    setIsNextBtnLoading(false);
    dispatch(onEventWizardIsEventBeingCreatedUpdate(false));

    if (showErrModal) {
      openErrorModal({
        message: window.i18next.t('pmwjs_error_message'),
        log: false
      });
    }
  }

  const resetLoadingStates = (): void => {
    setIsNextBtnLoading(false);
    dispatch(onEventWizardIsEventBeingCreatedUpdate(false));
  };

  const getNextStepLabel = (): string => {
    if (isLastStep()) {
      if (pageSourceType === EventWizardSourceType.UPDATE) {
        return window.i18next.t('pmwjs_update');
      }
      return window.i18next.t('pmwjs_create');
    }
    return window.i18next.t('pmwjs_next');
  };

  const goBackStep = (): void => {
    if (isFirstStep()) {
      return;
    }
    updateProgressStep(orderedProgressSteps[currentStepIndex - 1]);
    toggleDisplayPreviewRegistrationForm(false);

    if (!isCoverPhotoSectionComplete(coverPhoto)) {
      toggleDisplayPreviewCoverPhotoSection(false);
    }
  };

  const goToNextStep = (): void => {
    dispatch(onEventWizardIsStylesPanelOpenUpdate(false));
    setIsNextBtnLoading(true);

    if (!areRequiredSectionsComplete()) {
      dispatch(onEventWizardCheckErrorStateUpdate(true));
      dispatch(onEventWizardUpdateShouldExpandReviewSection(true));
      setIsNextBtnLoading(false);
      return;
    }
    dispatch(onEventWizardCheckErrorStateUpdate(false));

    if (!isLastStep()) {
      window.PMW.gtm.trackGA4CustomEvent(GA4EventName.EVENT_WIZARD_COMPLETE_FIRST_STEP);
    }

    if (isLastStep()) {
      dispatch(onEventWizardIsEventBeingCreatedUpdate(true));
      return;
    }
    updateProgressStep(orderedProgressSteps[currentStepIndex + 1]);
    toggleDisplayPreviewCoverPhotoSection();
    setIsNextBtnLoading(false);
  };

  const updateProgressStep = (step: EventWizardProgressStep): void => {
    dispatch(onEventWizardProgressStepUpdate(step));
  };

  const isFirstStep = (): boolean => {
    return currentStepIndex === 0;
  };

  const isLastStep = (): boolean => {
    return currentStepIndex === orderedProgressSteps.length - 1;
  };

  const areRequiredSectionsComplete = (): boolean => {
    if (orderedProgressSteps[currentStepIndex] === EventWizardProgressStep.OVERVIEW) {
      return isBasicSectionComplete(title, venue, startDateTime, endDateTime, timezone);
    }
    return (
      isBasicSectionComplete(title, venue, startDateTime, endDateTime, timezone) &&
      isMediaSectionComplete(desc, coverPhoto) &&
      isOrganiserSectionComplete(organisers.selectedOrganiser) &&
      isRegistrationsSectionComplete(registrationDetails)
    );
  };

  const getProgressSteps = (): ReactElement<any>|undefined => {
    if (pageSourceType === EventWizardSourceType.UPDATE) {
      return undefined;
    }

    return isSmallScreen ? (
        <ProgressDots currentStep={currentStepIndex + 1} numDots={orderedProgressSteps.length} />
      ) : (
        <SteppedProgressBar className="spacing-m-b-3" currentStep={currentStepIndex + 1} numSteps={orderedProgressSteps.length} labels={getStepLabels()} />
      )
  }

  const handleCancel = (): void => {
    window.history.back();
  };

  return (
    <div
      className={`${styles.footerContainer} ${isSmallScreen ? 'spacing-p-l-3 spacing-p-r-3' : 'spacing-p-l-12 spacing-p-r-12'} bg-neutral-0 flex-row-justify-center _full-width spacing-p-t-3 spacing-p-b-3 shadow-static-elevation-2 border-s-standard _no-border-right _no-border-left _no-border-bottom`}
    >
      <FeedbackButton
        id={TICKET_CHECKIN_SURVEY_HOTJAR_ID}
        uriToShowSurveyButton={TICKET_CHECKIN_SURVEY_URI_SHOW}
        uriToMarkSurveyAsCompleted={TICKET_CHECKIN_SURVEY_URI_MARK_COMPLETE}
        btnText={window.i18next.t('pmwjs_want_to_sell_tickets')}
        floatingFeedbackButtonProps={{bottom: 81, right: isSmallScreen ? 'max(12px, calc((100vw - 1024px) / 2))' : 'max(48px, calc((100vw - 1024px) / 2))'}}
      />
      <div className={`${styles.contentContainer} _full-width flex-row-align-center justify-content-between `}>
        <Button
          onClick={pageSourceType === EventWizardSourceType.UPDATE ? handleCancel : goBackStep}
          customClasses={pageSourceType === EventWizardSourceType.UPDATE ? '' : (isFirstStep() ? '_invisible' : '')}
          type={Type.SECONDARY}
          size={Size.SMALL}
          icon={pageSourceType === EventWizardSourceType.UPDATE ? undefined : "icon-arrow-left"}
          text={pageSourceType === EventWizardSourceType.UPDATE ? window.i18next.t('pmwjs_cancel') : window.i18next.t('pmwjs_back')}
        />

        {getProgressSteps()}

        <Button
          isLoading={isNextBtnLoading}
          disabled={shouldNextBtnBeDisabled}
          onClick={goToNextStep}
          data-testid={EventWizardTestId.FOOTER_PRIMARY_BUTTON}
          type={Type.PRIMARY}
          size={Size.SMALL}
          icon={pageSourceType === EventWizardSourceType.UPDATE ? undefined : "icon-arrow-right"}
          iconPlacement={IconPlacement.RIGHT}
          text={getNextStepLabel()}
        />
      </div>
    </div>
  );
}
